// The Follow button block: what both templates' renderers share.
//
// A visitor follows the organization with a TrailsIQ Go account — an
// emailed six-digit code, a name the first time, no password — and the
// follow is kept on the ACCOUNT, because a browser has no device to
// push to. The app follows the same organizations, with alerts, the
// first time that account signs in on a phone. lib/reportAccount.js is
// the one client for all of it; FollowDialog.vue is the sign-in.
//
// WHERE the account lives decides the one branch here:
//
//  - the public host (go.trailsiq.com/{slug}) and the /site/ preview:
//    same-origin. ResolveCustomDomain passes exactly the follow
//    endpoints through on the public host, so the session cookie is
//    ours and on a host we own.
//  - an organization's OWN domain: the account endpoints are NOT
//    served there, on purpose. Signing in sets a session cookie on the
//    host the page came from, and a hostname the customer controls the
//    DNS for is a hostname that can be pointed somewhere else and
//    collect it. So the button there opens the same page on the public
//    host with `#tiq-follow`, which opens the dialog on arrival.

import { computed, inject, onMounted, ref } from 'vue';
import { accountUser, accountFollows, loadAccountFollows, followOrg } from '@/lib/reportAccount.js';
import { storePlatform, reportAppIntentUrl } from '@/lib/storePlatform.js';
import { sitePrefix } from './siteLocation.js';

export const FOLLOW_HASH = '#tiq-follow';

export const FOLLOW_DEFAULTS = {
    heading: 'Get trail alerts',
    summary: 'Follow us to hear when trails open or close. Alerts arrive in the free TrailsIQ Go app.',
    button_label: 'Follow',
};

// One dialog per page, however many Follow blocks it carries: the
// arrival hash opens the first block to mount and nothing else.
let arrivalHandled = false;

export function useFollowBlock(props) {
    const editing = inject('tiqEditorCanvas', false);

    const slug = computed(() => props.org?.slug || '');
    const follow = computed(() => props.site_data?.site?.follow || {});
    // The organization can be followed at all (not hidden from the
    // public, not suspended). A block that could only ever say "that
    // organization can't be followed" is left off the live page.
    const followable = computed(() => follow.value.followable !== false);

    // Where the app is for alerts: the org's own page in it when the
    // app shows this org, the app's front door otherwise.
    const appUrl = computed(() => props.site_data?.go_url
        || `${String(props.site_data?.site?.app_url || '').replace(/\/$/, '')}/report`);

    const open = ref(false);
    const start = ref('email');
    const busy = ref(false);
    const ready = ref(false);

    // Decided once mounted: the server renders every page the same for
    // every visitor, and hydration keeps the server's markup.
    const onOwnDomain = ref(false);
    const isFollowing = computed(() => ready.value && Boolean(accountUser.value)
        && accountFollows.value.includes(slug.value));
    // On the org's own domain with no public host to send the visitor
    // to, the button could do nothing — and a control that does nothing
    // is worse than one that is absent.
    const canFollow = computed(() => followable.value
        && !(onOwnDomain.value && !follow.value.public_page));

    // The same page on the public host, for a visitor on the org's own
    // domain. Null when there is no public host (a box without one),
    // and then the block cannot follow from a custom domain at all.
    function handoffUrl() {
        const base = follow.value.public_page;
        if (!base || typeof window === 'undefined') return null;
        const path = window.location.pathname === '/' ? '' : window.location.pathname;
        return `${String(base).replace(/\/$/, '')}${path}${FOLLOW_HASH}`;
    }

    async function openFollow() {
        if (editing || busy.value || !slug.value) return;
        if (onOwnDomain.value) {
            const to = handoffUrl();
            if (to) window.location.href = to;
            return;
        }
        if (isFollowing.value) {
            start.value = 'following';
            open.value = true;
            return;
        }
        if (accountUser.value) {
            busy.value = true;
            try {
                await followOrg(slug.value);
                start.value = 'done';
            } catch {
                // Signed out since the page loaded, or refused: the
                // dialog starts at the sign-in and says what went wrong
                // from there.
                start.value = 'email';
                await loadAccountFollows().catch(() => {});
            } finally {
                busy.value = false;
            }
        } else {
            start.value = 'email';
        }
        open.value = true;
    }

    // Android: Chrome keeps a same-site tap in the browser, so "Get
    // TrailsIQ Go" opens the app by intent, or its Play Store page.
    function openApp(e) {
        if (storePlatform() !== 'android') return;
        try {
            const intent = reportAppIntentUrl(new URL(appUrl.value, window.location.href).href);
            e.preventDefault();
            window.location.href = intent;
        } catch { /* malformed url: let the plain link go */ }
    }

    onMounted(async () => {
        if (editing) return;
        // '' is the organization's own domain: the preview says
        // /site/{slug} and the public host /{slug}.
        onOwnDomain.value = sitePrefix() === '';
        if (onOwnDomain.value) {
            ready.value = true;
            return;
        }
        await loadAccountFollows().catch(() => {});
        ready.value = true;
        if (!arrivalHandled && window.location.hash === FOLLOW_HASH && followable.value) {
            arrivalHandled = true;
            // Leave the address as the page's own, so a reload or a
            // shared link does not reopen the dialog.
            try { history.replaceState(history.state, '', window.location.pathname + window.location.search); } catch { /* ignore */ }
            openFollow();
        }
    });

    return {
        editing, slug, followable, canFollow, appUrl, open, start, busy, ready, isFollowing, onOwnDomain,
        openFollow, openApp, handoffUrl,
    };
}
